/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This source code is the exclusive property of SKD RATWANI Technology &
 * Software Solutions Pvt Ltd, operating as "SkillDelivery". Unauthorized
 * copying, modification, distribution, or use of this software, via any
 * medium, is strictly prohibited without express written permission.
 *
 * For licensing inquiries: legal@skilldelivery.co.in
 *
 * Registered Office: Sitapur, Uttar Pradesh, India - 261001
 * Jurisdiction: Noida, Uttar Pradesh, India - 201301
 */

import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DOMAINS } from '../config/domains';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';

const ENTITIES = [
  {
    icon: '👤',
    title: 'Individual',
    subhead: "I'm a practitioner — engineer, implementer, consultant",
    tagline: 'Your personal expertise becomes visible.',
    to: '/entity/individual'
  },
  {
    icon: '🏢',
    title: 'Organization',
    subhead: "We're a company, team, or implementation partner",
    tagline: "Your team's collective knowledge becomes visible.",
    to: '/entity/organization'
  },
  {
    icon: '🎓',
    title: 'University',
    subhead: "We're an academic institution",
    tagline: "Your institution's depth becomes visible to industry.",
    to: '/entity/university'
  },
  {
    icon: '🏫',
    title: 'Institute',
    subhead: "We're a training, certification, or vocational body",
    tagline: 'Your programs and graduates become visible.',
    to: '/entity/institute'
  }
];

const WelcomeRouter = () => {
  const navigate = useNavigate();

  const goTo = (path) => () => {
    localStorage.setItem('skd_welcome_seen', 'true');
    navigate(path);
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-brand-bgOff via-white to-white">

      <PublicNav />

      {/* ─── Decorative radial glows ─────────────────────────────── */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-32 -right-32"
        style={{
          width: '500px',
          height: '500px',
          background:
            'radial-gradient(circle, rgba(92, 225, 230, 0.15) 0%, transparent 70%)'
        }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-24 -left-24"
        style={{
          width: '400px',
          height: '400px',
          background:
            'radial-gradient(circle, rgba(33, 150, 243, 0.08) 0%, transparent 70%)'
        }}
      />

      {/* ─── Content ─────────────────────────────────────────────── */}
      <div className="relative max-w-6xl mx-auto px-6 pt-16 sm:pt-20 pb-16">

        {/* ── Hero text ── */}
        <header className="text-center mt-12 sm:mt-16">
          <h1 className="text-5xl sm:text-6xl font-extrabold tracking-tighter leading-tight bg-gradient-to-br from-brand-cyan to-brand-blue bg-clip-text text-transparent">
            Skills, Delivered!
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-brand-text leading-relaxed max-w-2xl mx-auto font-semibold">
            You've built things the world should know about.
          </p>
          <p className="mt-3 text-base sm:text-lg text-brand-muted leading-relaxed max-w-2xl mx-auto">
            Let's start with who you are.
          </p>
        </header>

        {/* ── Eyebrow: WHO ARE YOU ── */}
        <p className="text-center mt-14 sm:mt-16 text-[11px] font-bold tracking-[2px] uppercase text-brand-cyan">
          Who are you?
        </p>

        {/* ── 2x2 entity grid ── */}
        <section
          aria-label="Choose your entity type"
          className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5"
        >
          {ENTITIES.map((e) => (
            <button
              key={e.to}
              type="button"
              onClick={goTo(e.to)}
              className="group text-left bg-white border border-brand-border rounded-brandCard p-6 transition-all duration-200 hover:border-brand-cyan hover:-translate-y-0.5 hover:shadow-brand focus:outline-none focus:ring-2 focus:ring-brand-cyan focus:ring-offset-2"
            >
              <div className="w-11 h-11 rounded-xl bg-brand-cyan/10 flex items-center justify-center mb-4">
                <span className="text-2xl leading-none" aria-hidden="true">{e.icon}</span>
              </div>
              <h3 className="font-bold text-base text-brand-text">
                {e.title}
              </h3>
              <p className="mt-1.5 text-sm text-brand-muted2 leading-relaxed">
                {e.subhead}
              </p>
              <p className="mt-3 text-sm italic text-brand-text2 leading-relaxed">
                {e.tagline}
              </p>
              <p className="mt-4 text-sm font-semibold text-brand-cyan group-hover:translate-x-1 transition-transform">
                Continue →
              </p>
            </button>
          ))}
        </section>

        {/* ── Manifesto link ── */}
        <p className="text-center mt-10 text-sm text-brand-muted">
          Not sure?{' '}
          <Link
            to="/manifesto"
            className="text-brand-cyan hover:text-brand-cyanDark font-medium hover:underline"
          >
            Read why SkillDelivery exists →
          </Link>
        </p>

        {/* ── Eyebrow: Or pick a domain ── */}
        <p className="text-center mt-16 text-[11px] font-bold tracking-[2px] uppercase text-brand-cyan">
          Or pick a domain
        </p>

        {/* ── Domain row ── */}
        <section
          aria-label="Browse by domain"
          className="mt-4 flex flex-wrap justify-center gap-3"
        >
          {DOMAINS.map((d) => (
            <button
              key={d.slug}
              type="button"
              onClick={goTo(`/domain/${d.slug}`)}
              className={
                d.featured
                  ? 'inline-flex items-center gap-1.5 px-6 py-3 rounded-brand bg-gradient-to-br from-brand-cyanLight to-brand-blue text-white font-semibold text-sm shadow-brandButton hover:shadow-brandButtonHover hover:-translate-y-px transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brand-cyan focus:ring-offset-2'
                  : 'inline-flex items-center gap-1.5 px-4 py-2.5 rounded-brand bg-white border-2 border-brand-border text-brand-text font-medium text-sm hover:border-brand-cyan hover:-translate-y-px transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brand-cyan focus:ring-offset-2'
              }
            >
              <span aria-hidden="true">{d.icon}</span>
              {d.shortLabel}
            </button>
          ))}
        </section>

        {/* ── Inline auth footer ── */}
        <footer className="text-center mt-12 text-sm text-brand-muted">
          Already have an account?{' '}
          <Link
            to="/login"
            className="text-brand-cyan hover:text-brand-cyanDark font-medium hover:underline"
          >
            Sign in
          </Link>
        </footer>

      </div>

      <PublicFooter />
    </div>
  );
};

export default WelcomeRouter;
